import React from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import debounce from 'lodash/debounce';
import {
  Grid,
  Card as MuiCard,
  IconButton,
  Modal,
  Backdrop,
  Fade
} from '@material-ui/core';
import {
  MoreVert,
  CloseRounded,
  Room,
  CropFreeSharp,
} from '@material-ui/icons';

import { ICardBackProps } from './card-back.interface';
import { cardConfig } from '../card.config';
import config from '../../../config/backend';
import EE from '../../../api/eventemitter';
import socket from '../../../api/socket';
import { categoryIcon } from '../../../utility/categoryIcon';
import { timeAgo } from '../../../utility/timeAgo';

export const CardBack = (props: ICardBackProps): React.ReactElement => {
  const {
    id,
    category,
    categoryColor,
    content,
    setContent,
    modTime,
    pinned = false,
    imgUrl,
    stack = false,
    stackIndex,
    flippedStack = false,
    contentInputRef,
    manageChecker = false,
    username,
    readonly = false,
    setReadonly,
    openContextMenu,
    updateCardContent,
    receiveTargetTextfield,
    classes,
  } = props;

  const debounceOnChange = debounce(() => {
    updateCardContent(stackIndex, content);
  }, 300);

  const handleContentInputChange = (event: ContentEditableEvent) => {
    const nextContent = event.target.value.substring(0, cardConfig.maxCharCount);
    setContent(nextContent);

    if (stack) {
      debounceOnChange();
      socket.emit('Object Live Typing', { objId: id, content: nextContent });
    }
  };

  const handleBlurContentInput = () => {
    socket.emit('Game Object Parse Links', { selectedIDs: [id] });
    setReadonly(true);
  };

  const [expandImg, setExpandImg] = React.useState(false);

  const expand = () => {
    setExpandImg(true)
  }

  const closeImg = () => {
     setExpandImg(false)
  }

  const whiteBg = (category === 'Blank' || !category || !categoryColor);
  const panelBg = whiteBg ? '#fff' : categoryColor;
  const onColor = whiteBg ? '#4f4f4f' : '#fff';

  return (
    <MuiCard
      style={{
        background: '#fff',
        border: manageChecker ? 'thick solid red' : undefined
      }}
      className={flippedStack ? classes.cardContentFlip : classes.cardContent}
    >
      <div className={classes.cardInnerPanel} style={{ background: panelBg }}>
        {/* HEADER: icon (left) + centered title + ellipsis (right) */}
        <div className={classes.cardHeaderRow}>
          {categoryIcon(category, categoryColor, { size: 22 })}
          <span className={classes.cardTitle} style={{ color: onColor }}>
            {category ? category : 'No category'}
            {pinned ? <Room className={classes.pin} style={{ color: onColor }} /> : null}
          </span>
          <IconButton className={classes.cardHeaderMenuBtn} onClick={openContextMenu}>
            <MoreVert style={{ color: whiteBg ? '#c4c4c4' : '#fff' }} />
          </IconButton>
        </div>

        {/* BODY: content editor, or full-bleed image */}
        <Grid container className={classes.contentWrapper}>
          {
            imgUrl ?
              <div className={classes.imgFill}>
                <IconButton className={classes.imgDelete} style={{ outline: 'none' }}>
                  <CloseRounded style={{ fontSize: '12px' }} />
                </IconButton>
                <IconButton
                  onClick={(e)=>{e.currentTarget.blur(); expand()}}
                  style={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }}
                ><CropFreeSharp style={{ fontSize: '18px', color: '#fff' }} /></IconButton>
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  alt="img"
                  src={`${config.backend.uri}/static/uploads/${imgUrl}`}
                />
                <Modal
                  open={expandImg}
                  onClose={closeImg}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  BackdropProps={{ timeout: 300 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Fade in={expandImg}>
                    <img
                      src={`${config.backend.uri}/static/uploads/${imgUrl}`}
                      alt="Full Preview"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        maxWidth: '90%',
                        maxHeight: '90%',
                        borderRadius: 8,
                        boxShadow: '0 0 20px rgba(0,0,0,0.5)',
                      }}
                    />
                  </Fade>
                </Modal>
              </div>
              :
              <ContentEditable
                innerRef={contentInputRef}
                id="contentEditable"
                className={classes.content /*+ " cancel"*/}
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  zIndex: 1,
                }}
                html={content}
                onKeyDown={(e) => {
                  if (e.keyCode === 13 && !e.shiftKey) {
                    // prevent shift + enter new lin
                    e.preventDefault();
                  }
                }}
                onClick={(evt) => {
                  evt.persist()
                  if (evt?.target && category === 'Blank' && stack) {
                    receiveTargetTextfield(evt.target)
                    EE.emit('Canvas Defocus')
                  }
                  if ((evt.target as HTMLAnchorElement).tagName.toLowerCase() === 'a') {
                    const newWindow = window.open((evt.target as HTMLAnchorElement).href, '_blank', 'noopener noreferrer')
                    if (newWindow) { newWindow.opener = null }
                  }
                }}
                onChange={handleContentInputChange}
                onBlur={handleBlurContentInput}
                disabled={
                  Boolean(imgUrl) || readonly
                }
              />
          }
        </Grid>

        {/* FOOTER: "Name · N min" (overlays the image on image cards) */}
        <div className={classes.cardFooter} style={{ color: onColor }}>
          {username} · {timeAgo(modTime)}
        </div>
      </div>
    </MuiCard>
  );
};
